// src/components/Payout/components/PayoutHistory/component.js
import React, { useRef, useEffect, useState } from "react";
import { FiSearch, FiCalendar, FiX, FiArrowLeft, FiTrash2, FiCheckCircle, FiAlertCircle, FiCopy } from 'react-icons/fi';
import { BsClipboardData } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import styles from './PayoutHistory.module.css';

// Delete Confirmation Modal
const DeleteModal = ({ show, filename, onConfirm, onCancel, deleting }) => {
  if (!show) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.deleteModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.deleteIconWrapper}>
          <FiAlertCircle className={styles.deleteIcon} />
        </div>
        <h3 className={styles.deleteTitle}>Delete Record</h3>
        <p className={styles.deleteMessage}>
          Are you sure you want to delete this payout record?
        </p>
        <p className={styles.deleteFilename}>
          File: <strong>{filename}</strong>
        </p>
        <p className={styles.deleteWarning}>Once deleted, this data cannot be recovered.</p>
        <div className={styles.deleteActions}>
          <button className={styles.deleteCancel} onClick={onCancel} disabled={deleting}>
            Cancel
          </button>
          <button className={styles.deleteConfirm} onClick={onConfirm} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Yes, Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Success Popup
const SuccessPopup = ({ show, message, onClose }) => {
  if (!show) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.successModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.successIconWrapper}>
          <FiCheckCircle className={styles.successIcon} />
        </div>
        <h3 className={styles.successTitle}>Deleted Successfully</h3>
        <p className={styles.successMessage}>{message || 'File has been deleted successfully'}</p>
        <button className={styles.successBtn} onClick={onClose}>
          OK
        </button>
      </div>
    </div>
  );
};

const PayoutHistoryComponent = ({
  uploads = [],
  loading,
  deleting,
  error,
  searchTerm,
  onSearchChange,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  showDateFilter,
  onToggleDateFilter,
  onClearDateFilter,
  onUploadNew,
  formatDisplayDate,
  onDeleteClick,
  deleteModal,
  onDeleteConfirm,
  onDeleteCancel,
  successPopup,
  onSuccessPopupClose
}) => {
  const navigate = useNavigate();
  const dateFilterRef = useRef(null);
  const [copiedId, setCopiedId] = useState(null);

  const handleBack = () => navigate(-1);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateFilterRef.current && !dateFilterRef.current.contains(event.target)) {
        if (showDateFilter) onToggleDateFilter();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDateFilter, onToggleDateFilter]);

  const handleCopy = (id) => {
    navigator.clipboard.writeText(id.toString());
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (loading) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading payout history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      {/* Header */}
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={handleBack}>
          <FiArrowLeft className={styles.backIcon} />
        </button>
        <h1 className={styles.title}>Payout History</h1>
        <span className={styles.totalCount}>{uploads.length} Records</span>
      </div>

      {/* Search and Filter Section */}
      <div className={styles.controls}>
        <div className={styles.searchWrapper}>
          <FiSearch className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search by ID or file name..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          {/* {searchTerm && (
            <button className={styles.clearSearch} onClick={() => onSearchChange('')}>
              <FiX />
            </button>
          )} */}
        </div>

        <div className={styles.filterWrapper} ref={dateFilterRef}>
          <button
            className={`${styles.filterBtn} ${showDateFilter ? styles.filterActive : ''}`}
            onClick={onToggleDateFilter}
          >
            <FiCalendar className={styles.filterIcon} />
            <span>Filter by Date</span>
          </button>

          {showDateFilter && (
            <div className={styles.dateFilterPopup}>
              <div className={styles.dateFilterHeader}>
                <span>Select Date Range</span>
                <button className={styles.dateFilterClose} onClick={onToggleDateFilter}>
                  <FiX />
                </button>
              </div>
              <div className={styles.dateFilterBody}>
                <div className={styles.dateInputGroup}>
                  <label>From</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={onStartDateChange}
                    max={endDate || undefined}
                  />
                </div>
                <div className={styles.dateInputGroup}>
                  <label>To</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={onEndDateChange}
                    min={startDate || undefined}
                  />
                </div>
              </div>
              {(startDate || endDate) && (
                <div className={styles.dateFilterFooter}>
                  <button className={styles.clearFilterBtn} onClick={onClearDateFilter}>
                    Clear Filter
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <button className={styles.uploadBtn} onClick={onUploadNew}>
          + Upload New
        </button>
      </div>

      {/* Active Filters */}
      {(startDate || endDate) && (
        <div className={styles.activeFilters}>
          <span className={styles.activeFilterLabel}>Active Filters:</span>
          {startDate && (
            <span className={styles.filterTag}>
              From: {moment(startDate).format('DD MMM YYYY')}
              <button onClick={() => onStartDateChange({ target: { value: '' } })}>
                <FiX />
              </button>
            </span>
          )}
          {endDate && (
            <span className={styles.filterTag}>
              To: {moment(endDate).format('DD MMM YYYY')}
              <button onClick={() => onEndDateChange({ target: { value: '' } })}>
                <FiX />
              </button>
            </span>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className={styles.errorMessage}>
          <FiAlertCircle className={styles.errorIcon} />
          <span>{error}</span>
        </div>
      )}

      {/* Table */}
      <div className={styles.tableWrapper}>
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.colSno}>S.No.</th>
                <th className={styles.colDate}>Payout Date</th>
                <th className={styles.colId}>ID</th>
                <th className={styles.colFile}>File Name</th>
                <th className={styles.colStatus}>Status</th>
                <th className={styles.colAction}>Action</th>
              </tr>
            </thead>
            <tbody>
              {uploads.length > 0 ? (
                uploads.map((upload, index) => (
                  <tr key={upload.id || index} className={styles.tableRow}>
                    <td className={styles.colSno}>{index + 1}</td>
                    <td className={styles.colDate}>{formatDisplayDate(upload.payoutDate || upload.date || upload.createdAt)}</td>
                    <td className={styles.colId}>
                      <span className={styles.idBadge}>{upload.id}</span>
                      <button
                        className={styles.copyBtn}
                        onClick={() => handleCopy(upload.id)}
                        title="Copy ID"
                      >
                       
                      </button>
                    </td>
                    <td className={styles.colFile}>
                      <BsClipboardData className={styles.fileIcon} />
                      <span className={styles.fileName}>{upload.filename || upload.name || upload.file || 'N/A'}</span>
                    </td>
                    <td className={styles.colStatus}>
                      <span className={`${styles.statusBadge} ${styles.statusSettled}`}>
                        {upload.status || 'SETTLED'}
                      </span>
                    </td>
                    <td className={styles.colAction}>
                      <button
                        className={`${styles.actionBtn} ${styles.deleteBtn}`}
                        onClick={() => onDeleteClick(upload.id, upload.filename || upload.name || 'file')}
                        title="Delete"
                        disabled={deleting}
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className={styles.emptyState}>
                    <div className={styles.emptyContent}>
                      <BsClipboardData className={styles.emptyIcon} />
                      <p>No payout records found</p>
                      <button className={styles.emptyUploadBtn} onClick={onUploadNew}>
                        + Upload New File
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      <DeleteModal
        show={deleteModal.show}
        filename={deleteModal.filename}
        onConfirm={onDeleteConfirm}
        onCancel={onDeleteCancel}
        deleting={deleting}
      />

      <SuccessPopup
        show={successPopup.show}
        message={successPopup.message}
        onClose={onSuccessPopupClose}
      />
    </div>
  );
};

export default PayoutHistoryComponent;